import React, { useState, useMemo, useCallback } from 'react'
import {
  Avatar,
  Box,
  ButtonBase,
  List,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useWebSocket } from 'hooks/websocket/useWebSocket'
import { useUsers } from 'hooks/useUsers'
import { useNavigate } from 'react-router-dom'
import { PrivateUserInfoType } from '@otw/models'
import { OnlineUser } from '@otw/models'
import { CHIP_FONT, CHIP_HEIGHT_DENSE, TOUCH_MIN } from 'core/theme.tokens'

export const OnlineUsersDisplay = ({ compact = false }: { compact?: boolean }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { users, isLoading } = useUsers()
  const { onlineUsers } = useWebSocket()

  const open = Boolean(anchorEl)
  const activeUsers = useMemo(
    () =>
      (Object.entries(onlineUsers).filter(([_, data]) => data.online) as [string, OnlineUser][]).sort(
        ([_, a], [__, b]) => (b.lastPing ?? 0) - (a.lastPing ?? 0),
      ),
    [onlineUsers],
  )

  const previewUsers = activeUsers.slice(0, compact ? 3 : 5)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const getUserName = useCallback(
    (userId: string) => {
      const user = users?.find(u => u.id === userId) as PrivateUserInfoType | undefined
      if (isLoading) return 'Loading...'
      return user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.email ?? 'Unknown User'
    },
    [users, isLoading],
  )

  const handleLocationClick = (path: string, e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(path)
    handleClose()
  }

  const formatPagePath = useCallback((path: string) => {
    const parts = path.replace(/^\//, '').split('/')
    return parts
      .map(part =>
        part
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
      )
      .join(' → ')
  }, [])

  const formatTimestamp = useCallback((timestamp?: number) => {
    if (!timestamp) return 'Unknown'
    const now = Date.now()
    const diff = now - timestamp
    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return new Date(timestamp).toLocaleDateString('en-US')
  }, [])

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={activeUsers.length === 1 ? '1 online' : `${activeUsers.length} online`}>
        <ButtonBase
          onClick={handleClick}
          aria-label={`${activeUsers.length} online users`}
          aria-haspopup='dialog'
          aria-expanded={open}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            minHeight: { xs: TOUCH_MIN, md: CHIP_HEIGHT_DENSE + 8 },
            px: 0.75,
            py: 0.25,
            borderRadius: 1.5,
            color: 'inherit',
            opacity: 0.92,
            '&:hover': {
              opacity: 1,
              bgcolor: alpha(theme.custom.chromeContrast, 0.1),
            },
          }}
        >
          <Stack direction='row' sx={{ pl: previewUsers.length > 0 ? 0.25 : 0 }}>
            {previewUsers.map(([userId], index) => {
              const user = users?.find(u => u.id === userId)
              return (
                <Avatar
                  key={userId}
                  src={user?.profilePicture || undefined}
                  alt={getUserName(userId)}
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: 'success.main',
                    border: '1.5px solid',
                    borderColor: 'transparent',
                    ml: index === 0 ? 0 : -0.75,
                    zIndex: previewUsers.length - index,
                  }}
                >
                  {(user?.displayName || getUserName(userId))?.[0]}
                </Avatar>
              )
            })}
            {activeUsers.length === 0 ? (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.45),
                }}
              />
            ) : null}
          </Stack>
          <Typography
            component='span'
            sx={{
              fontSize: CHIP_FONT,
              fontWeight: 600,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {activeUsers.length}
            <Box component='span' sx={{ opacity: 0.72, fontWeight: 500, ml: 0.4 }}>
              online
            </Box>
          </Typography>
        </ButtonBase>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              width: 320,
              maxHeight: 420,
              overflow: 'hidden',
              borderRadius: 2,
              mt: 0.75,
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant='subtitle2' fontWeight={700}>
            Online
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {activeUsers.length} active
          </Typography>
        </Box>

        <List dense disablePadding sx={{ py: 0.5, maxHeight: 360, overflow: 'auto' }}>
          {activeUsers.length === 0 ? (
            <Box sx={{ px: 1.5, py: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Nobody else is online right now.
              </Typography>
            </Box>
          ) : (
            activeUsers.map(([userId, data]) => {
              const user = users?.find(u => u.id === userId)
              return (
                <ListItemButton
                  key={userId}
                  sx={{ py: 1, px: 1.5, alignItems: 'flex-start', gap: 1.25 }}
                >
                  <Box sx={{ position: 'relative', mt: 0.25 }}>
                    <Avatar
                      src={user?.profilePicture || undefined}
                      sx={{ width: 32, height: 32, bgcolor: 'success.main', fontSize: '0.85rem' }}
                    >
                      {(user?.displayName || getUserName(userId))?.[0]}
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -1,
                        right: -1,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        border: '2px solid',
                        borderColor: 'background.paper',
                      }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant='body2'
                      fontWeight={600}
                      noWrap
                      sx={{ lineHeight: 1.25 }}
                    >
                      {getUserName(userId)}
                    </Typography>
                    {data.currentPage ? (
                      <Typography
                        onClick={e => data.currentPage && handleLocationClick(data.currentPage, e)}
                        variant='caption'
                        color='text.secondary'
                        noWrap
                        sx={{
                          display: 'block',
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                        }}
                      >
                        {formatPagePath(data.currentPage)}
                      </Typography>
                    ) : null}
                    <Typography variant='caption' color='success.main' sx={{ fontWeight: 500 }}>
                      {formatTimestamp(data.lastPing)}
                    </Typography>
                  </Box>
                </ListItemButton>
              )
            })
          )}
        </List>
      </Popover>
    </Box>
  )
}
